import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Array } from '@blade/std-lib'
import {} from '@root/entities/premium/ui/desktop'
import { CommentsHistoryLoading } from '@root/features/comment/ui/comments-history/comments-history-loading'
import { ProfileOwner } from '@root/features/user/profile/model/profile-owner'
import { ProfileTab } from '@root/features/user/profile/model/profile-tabs'
import { TransactionHistoryListLoading } from '@root/features/user/transaction-history/ui/transaction-history-list-loading'
import { UserConnectionsItem } from '@root/features/user/user-connections/ui/user-connections-item/user-connections-item'
import { Skeleton } from '@root/shared/ui-kit'
import { CurrencyCardCompactSkeleton } from '@root/widgets/fun/currency-card/ui/currency-card-compact-skeleton'

import { ProfileBoardStatistics } from './board/profile-board-statistics'

import type { DelugeId } from '@root/entities/user-short/model/DelugeId'

export type ProfileBoardContentTab = ProfileTab

type ProfileBoardContentSkeletonProps = {
  tab?: ProfileBoardContentTab
  delugeId: DelugeId
}

const placeholders = Array.range(1, 3)
const connectionPlaceholders = Array.range(1, 6)

export const ProfileBoardContentSkeleton = ({
  tab = ProfileTab.Statistics,
  delugeId,
}: ProfileBoardContentSkeletonProps) =>
  match(tab)
    .with(ProfileTab.Statistics, () => (
      <ProfileBoardStatistics
        delugeId={delugeId}
        owner={ProfileOwner.CurrentUser}
      />
    ))
    .with(ProfileTab.Coins, () => (
      <CoinsRoot>
        <CoinsHeader>
          <CoinsFilter>
            <Skeleton width={180} height={28} />
            <Skeleton width={220} height={28} />
            <Skeleton width={96} height={28} />
          </CoinsFilter>
        </CoinsHeader>
        <CoinsBody>
          {placeholders.map((item) => (
            <CurrencyCardCompactSkeleton key={item} />
          ))}
        </CoinsBody>
      </CoinsRoot>
    ))
    .with(ProfileTab.Followers, () => <UserConnectionsSkeleton />)
    .with(ProfileTab.Following, () => <UserConnectionsSkeleton />)
    .with(ProfileTab.Transactions, () => (
      <TransactionsRoot>
        <TransactionHistoryListLoading withWallet />
      </TransactionsRoot>
    ))
    .with(ProfileTab.Comments, () => (
      <CommentsRoot>
        <CommentsHeading>
          <Skeleton width={180} height={28} />
          <Skeleton width={240} height={28} />
        </CommentsHeading>
        <CommentsHistoryLoading />
      </CommentsRoot>
    ))
    .with(ProfileTab.Referral, () => (
      <ReferralRoot>
        <Skeleton width="100%" height={120} />
        <Skeleton width="100%" height={240} />
      </ReferralRoot>
    ))
    .exhaustive()

const UserConnectionsSkeleton = () => (
  <ConnectionsRoot>
    {connectionPlaceholders.map((item) => (
      <UserConnectionsItem.Skeleton key={item} withoutCoinsCount />
    ))}
  </ConnectionsRoot>
)

const CoinsRoot = styled.div`
  overflow: auto;
  padding: 0 12px 12px;
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  min-height: 0;
`

const CoinsHeader = styled.div`
  position: sticky;
  padding: 12px 0;
  top: 0;
  z-index: 1;
  background: var(--background-1);
`

const CoinsFilter = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: space-between;
`

const CoinsBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 12px;
  align-content: start;
`

const ConnectionsRoot = styled.div`
  padding: 10px 16px 12px 6px;
  display: grid;
  gap: 8px;
  overflow: auto;
  height: 100%;
  min-height: 0;
`

const TransactionsRoot = styled.div`
  overflow: auto;
  height: 100%;
  min-height: 0;
`

const CommentsRoot = styled.div`
  padding: 16px 16px 10px;
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 10px;
  height: 100%;
  min-height: 0;
  overflow: auto;
`

const CommentsHeading = styled.header`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  gap: 10px;
`

const ReferralRoot = styled.div`
  padding: 12px;
  display: grid;
  gap: 12px;
  height: 100%;
  min-height: 0;
  overflow: auto;
`
